<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usePlatformHeader } from '@moya/platform-shell-sdk/vue';
import {
  HomeIcon,
  UsersIcon,
  CubeIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  KeyIcon,
  LockClosedIcon,
  FingerPrintIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const sidebarOpen = ref(false);

const navigation = computed(() => [
  { name: 'Dashboard', href: '/', icon: HomeIcon, current: route.path === '/', permission: null },
  { name: 'Users', href: '/users', icon: UsersIcon, current: route.path.startsWith('/users'), permission: 'users:read' },
  { name: 'Roles', href: '/roles', icon: ShieldCheckIcon, current: route.path.startsWith('/roles'), permission: 'roles:read' },
  { name: 'Groups', href: '/groups', icon: UserGroupIcon, current: route.path.startsWith('/groups'), permission: 'groups:read' },
  { name: 'Apps', href: '/apps', icon: CubeIcon, current: route.path.startsWith('/apps'), permission: 'apps:read' },
  { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon, current: route.path.startsWith('/organizations'), permission: 'organizations:read' },
  { name: 'Audit Logs', href: '/audit-logs', icon: DocumentTextIcon, current: route.path.startsWith('/audit-logs'), permission: 'audit:read' },
  { name: 'API Keys', href: '/api-keys', icon: KeyIcon, current: route.path.startsWith('/api-keys'), permission: 'api-keys:read' },
  { name: 'Security', href: '/security', icon: FingerPrintIcon, current: route.path.startsWith('/security'), permission: 'security:manage' },
  { name: 'Permissions', href: '/permissions', icon: LockClosedIcon, current: route.path.startsWith('/permissions'), permission: 'permissions:read' },
].filter(item => !item.permission || authStore.hasPermission(item.permission)));

const authToken = computed(() => localStorage.getItem('moya_token') || '');
const apiUrl = 'https://identity.moyasolutions.com/api/v1';

// Platform Shell header wiring is delegated to the canonical SDK Vue composable
// (@moya/platform-shell-sdk/vue). It sets the header's props imperatively and
// attaches/cleans up the moya-* event listeners (and cross-tab token sync).
// App-specific behaviour is preserved via overrides:
//   - onLogout delegates to the Pinia auth store (which redirects to SSO login)
//   - onAppSwitch keeps the existing direct window.location redirect
const headerEl = ref<HTMLElement>();
usePlatformHeader(
  headerEl,
  reactive({
    appName: 'Admin Portal',
    appId: 'admin-portal',
    portalUrl: 'https://login.moyasolutions.com',
    apiUrl,
    token: authToken,
    notificationsUrl: 'wss://notify.moyasolutions.com/ws/notifications',
    showSearch: true,
    searchApiUrl: 'https://identity.moyasolutions.com/api/v1/portal/search',
    showMessenger: true,
    messengerUrl: 'wss://messenger.moyasolutions.com/api/v1/ws',
    messengerApiUrl: 'https://messenger.moyasolutions.com/api/v1',
    onLogout: () => authStore.logout(),
    onAppSwitch: (event: CustomEvent) => {
      const { url } = (event.detail ?? {}) as { url?: string };
      if (url) window.location.href = url;
    },
  }),
);
</script>

<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Platform Shell Header (props + moya-* events wired by usePlatformHeader) -->
    <moya-platform-header
      ref="headerEl"
      class="fixed top-0 left-0 right-0 z-50"
    />

    <!-- Mobile sidebar backdrop -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
      @click="sidebarOpen = false"
    />

    <!-- Mobile sidebar -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-white lg:hidden"
      style="top: 56px"
    >
      <div class="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
        <nav class="flex-1 space-y-1 px-2">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.href"
            :class="[
              item.current
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'group flex items-center px-3 py-3 text-sm font-medium rounded-md min-h-[44px]',
            ]"
            @click="sidebarOpen = false"
          >
            <component
              :is="item.icon"
              :class="[
                item.current ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500',
                'mr-3 h-5 w-5 flex-shrink-0',
              ]"
            />
            {{ item.name }}
          </router-link>
        </nav>
      </div>
    </div>

    <!-- Desktop sidebar -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col" style="top: 56px">
      <div class="flex flex-1 flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5 pb-4">
        <div class="flex flex-shrink-0 items-center px-4">
          <h1 class="text-xl font-bold text-gray-900">Admin Portal</h1>
        </div>
        <nav class="mt-5 flex-1 space-y-1 px-2">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.href"
            :class="[
              item.current
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
            ]"
          >
            <component
              :is="item.icon"
              :class="[
                item.current ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500',
                'mr-3 h-5 w-5 flex-shrink-0',
              ]"
            />
            {{ item.name }}
          </router-link>
        </nav>
      </div>
    </div>

    <!-- Main content area -->
    <div class="lg:pl-64" style="padding-top: 56px">
      <!-- Mobile header -->
      <div class="sticky top-14 z-10 flex h-16 flex-shrink-0 bg-white shadow lg:hidden">
        <button
          type="button"
          class="flex items-center justify-center w-12 min-h-[44px] text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <Bars3Icon class="h-6 w-6" />
        </button>
        <div class="flex flex-1 items-center justify-between px-4">
          <h1 class="text-lg font-semibold text-gray-900">Admin Portal</h1>
        </div>
      </div>

      <!-- Page content -->
      <main class="py-6">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>
